import { useMemo } from 'react';

import { Button } from '@gn-audio/jss-components';
import { AvailabilityState, Group, ProductType, SegmentType } from '@gn-audio/jss-core';

import { StandardPriceModel, WithPrice } from 'api/pricesApi/pricesModel';
import { FamilyGroup, SegmentTypes } from 'api/productCatalogueApi';
import {
  getAttributeValue,
  getAvailabilityState,
  MetaProducttypeAttribute,
  ProductPortfolioAttribute,
} from 'composables/Product/Buy/customizerHelpers';
import { LowestMonthPrice } from 'composables/Product/Buy/Price/LowestMonthPrice';
import { Price } from 'composables/Product/Buy/Price/Price';
import { sumPrices } from 'composables/Product/Buy/Price/priceHelpers';
import { ProductAction } from 'composables/Product/Buy/ProductAction/ProductAction';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { isNotNil } from 'utils/utils';

export interface CustomizerTotalProps {
  price?: StandardPriceModel | null;
  selectedGroup?: FamilyGroup;
  accessories?: WithPrice<Group>[];
}

export const CustomizerTotal = ({
  price,
  selectedGroup,
  accessories = [],
}: CustomizerTotalProps) => {
  const { t } = useFallbackTranslation();
  const availability = getAvailabilityState(price);
  const isAvailable = isNotNil(availability) && availability < AvailabilityState.WhereToBuy;

  const productCategory = useMemo(
    () => getAttributeValue(selectedGroup, MetaProducttypeAttribute) ?? '',
    [selectedGroup]
  );

  const productBrand = useMemo(
    () => getAttributeValue(selectedGroup, ProductPortfolioAttribute) ?? '',
    [selectedGroup]
  );

  const accessoryPrices = accessories.map(({ price }) => price);

  const totalPrice =
    price !== null ? sumPrices([price, ...accessoryPrices].filter(isNotNil)) : null;
  const accessorySkus = accessories.map(({ product }) => product.sku);
  const showRetailers = availability === AvailabilityState.WhereToBuy;
  const showComingSoon = availability === AvailabilityState.ComingSoon;

  const getComingSoonDictionaryKey = (segmentType?: SegmentType): string | undefined => {
    switch (segmentType) {
      case SegmentTypes.Consumer:
        return 'KeepMeUpdated';
      case SegmentTypes.Business:
      case SegmentTypes.Convergence:
      case SegmentTypes.Unknown:
      default:
        return 'ContactSales';
    }
  };

  const comingSoonTranslation = `system.${getComingSoonDictionaryKey(selectedGroup?.segmentType)}`;

  const isCurrentlyUnavailableAccessory =
    productCategory === 'Accessory' &&
    isNotNil(availability) &&
    availability !== AvailabilityState.Add;

  if (isCurrentlyUnavailableAccessory) {
    return (
      <div className="flex flex-col items-start gap-6">
        <div className="flex w-full justify-between text-[1.25rem]">
          {isAvailable && <span className="font-bold">{t('system.CgCustomizerTotal')}</span>}
          <Price
            segmentType={selectedGroup?.segmentType}
            productType={ProductType.Product}
            price={totalPrice}
          />
        </div>
        <div className="info-msg">{t('system.CurrentlyUnavailableAccessory')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-6">
      {price === undefined && (
        <>
          <div className="flex w-full justify-between text-[1.25rem]" aria-hidden>
            <span className="animate-thin-glow font-bold">{t('system.CgCustomizerTotal')}</span>
            <Price productType={ProductType.Product} price={undefined} />
          </div>
          <div className="animate-glow w-full" aria-hidden>
            <Button className="invisible w-full" variant="primary" size="l">
              <span>xxxxx xxxx xxx</span>
            </Button>
          </div>
        </>
      )}
      {isAvailable && price && selectedGroup && (
        <>
          {accessories?.length > 0 && (
            <div className="flex w-full justify-between">
              <span className="">{selectedGroup.productName}</span>
              <Price
                segmentType={selectedGroup.segmentType}
                productType={ProductType.Product}
                price={price}
              />
            </div>
          )}
          {accessories?.map(({ product, price: accessoryPrice }) => {
            return (
              <div key={product.productId} className="flex w-full justify-between">
                <span className="">{product.productName}</span>
                <Price
                  segmentType={product.segmentType}
                  productType={ProductType.Product}
                  price={accessoryPrice}
                />
              </div>
            );
          })}

          <div className="flex w-full justify-between text-[1.25rem]">
            <span className="font-bold">{t('system.CgCustomizerTotal')}</span>
            <Price
              segmentType={selectedGroup.segmentType}
              productType={ProductType.Product}
              price={totalPrice}
            />
          </div>
          <div className="w-full">
            <ProductAction
              productSku={selectedGroup.sku}
              accessorySkus={accessorySkus}
              price={totalPrice}
              segmentType={selectedGroup.segmentType}
              productCategory={productCategory}
              productName={selectedGroup.name}
              productBrand={productBrand}
            />
          </div>
          <LowestMonthPrice price={price} />
        </>
      )}
      {!isAvailable && (
        <span className="simpleBadge md:hidden">
          {availability === AvailabilityState.ComingSoon
            ? t('system.PriceComingSoon')
            : t('system.SoldOut')}
        </span>
      )}
      {showRetailers && selectedGroup && (
        <div className="w-full">
          <ProductAction
            productSku={selectedGroup.sku}
            accessorySkus={accessorySkus}
            price={totalPrice}
            segmentType={selectedGroup.segmentType}
            productCategory={productCategory}
            productName={selectedGroup.name}
            productBrand={productBrand}
          />
        </div>
      )}
      {showComingSoon && (
        <Button asLink href="/contact/jabra-sales" className="w-full" variant="primary" size="l">
          <span>{t(comingSoonTranslation)}</span>
        </Button>
      )}
    </div>
  );
};
